<div class="flex flex-col gap-4">
  <div class="flex items-start justify-between gap-4">
    <h2 class="m-0 text-lg font-bold text-midnight">
      @if (isEntry) {
      {{ isEditMode ? 'Edit entry' : 'Add entry' }}
      } @else if (isCancelAct) {
      {{ isEditMode ? 'Edit cancellation act' : 'Add cancellation act' }}
      }
    </h2>
    <button type="button" mat-dialog-close aria-label="Close dialog">
      <img src="assets/icons/close.svg" alt="Close" class="w-4 h-4" />
    </button>
  </div>

  <!-- Form -->
  @if (isEntry) {
  <ng-container *ngTemplateOutlet="entryForm"></ng-container>
  } @else if (isCancelAct) {
  <ng-container *ngTemplateOutlet="cancelActForm"></ng-container>
  }
</div>

<!-- Entry form -->
<ng-template #entryForm>
  <form [formGroup]="form" (ngSubmit)="onSubmit()" class="grid gap-4">
    <div class="grid grid-cols-2 gap-4">
      <et-atoms-select-field class="w-full">
        <label>Notarial act</label>
        <et-atoms-select-input formControlName="notorialAct" required>
          @for (option of notorialActOptions; track option) {
          <et-atoms-select-option [value]="option">
            {{ option }}
          </et-atoms-select-option>
          }
        </et-atoms-select-input>
        @if ( form.controls.notorialAct.touched &&
        form.controls.notorialAct.invalid ) {
        <span etAtomsSelectError>Notarial act is required</span>
        }
      </et-atoms-select-field>

      <et-atoms-select-field class="w-full">
        <label>Signer</label>
        <et-atoms-select-input formControlName="signerId" required>
          @for (option of signers(); track option.eJournalSignerId) {
          <et-atoms-select-option [value]="option.eJournalSignerId">
            {{ option.name }}
          </et-atoms-select-option>
          } @empty {
          <et-atoms-select-option [value]="" [disabled]="true">
            No signers added yet
          </et-atoms-select-option>
          }
        </et-atoms-select-input>
        @if ( form.controls.signerId.touched && form.controls.signerId.invalid )
        {
        <span etAtomsSelectError>Signer is required</span>
        }
      </et-atoms-select-field>

      <et-atoms-input class="w-full">
        <label>Document name</label>
        <input
          etAtomsInput
          formControlName="documentDescription"
          type="text"
          required
        />
        @if ( form.controls.documentDescription.touched &&
        form.controls.documentDescription.invalid ) {
        <span etAtomsInputError>Document description is required</span>
        }
      </et-atoms-input>

      <et-atoms-input class="w-full">
        <label>Page number</label>
        <input
          etAtomsInput
          formControlName="pageNumber"
          type="number"
          min="1"
          step="1"
        />
      </et-atoms-input>

      <et-atoms-input class="w-full">
        <label>Fee charged</label>
        <input
          etAtomsInput
          formControlName="feeCharged"
          type="number"
          min="0"
          step="0.01"
          required
        />
        @if ( form.controls.feeCharged.touched &&
        form.controls.feeCharged.invalid ) {
        <span etAtomsInputError>Fee charged is required</span>
        }
      </et-atoms-input>

      <div class="w-full col-span-2 px-[2px]">
        <label class="block mb-1 text-sm font-medium text-secondary"
          >Notes</label
        >
        <textarea
          formControlName="notes"
          rows="3"
          class="w-full px-6 py-3 font-medium border rounded-lg focus:outline-midnight text-midnight"
        ></textarea>
      </div>
    </div>

    @if (error(); as err) {
    <p class="text-error m-0">{{ err }}</p>
    }

    <div class="flex flex-wrap justify-end gap-4">
      <button
        type="button"
        class="normal-case rounded-full btn btn-outline min-w-[184px] text-base"
        mat-dialog-close
        [disabled]="isLoading()"
      >
        Cancel
      </button>
      <button
        type="submit"
        class="normal-case rounded-full btn btn-primary min-w-[184px] text-base"
        [class.pointer-events-none]="isLoading()"
      >
        @if (isLoading()) {
        <et-atoms-spinner size="small"></et-atoms-spinner>
        } @else { Save }
      </button>
    </div>
  </form>
</ng-template>

<!-- Cancel act form -->
<ng-template #cancelActForm>
  <form [formGroup]="form" (ngSubmit)="onSubmit()" class="grid gap-4">
    <div class="grid grid-cols-2 gap-4">
      <et-atoms-select-field class="w-full col-span-2">
        <label>Signer</label>
        <et-atoms-select-input formControlName="signerId" required>
          @for (option of signers(); track option.eJournalSignerId) {
          <et-atoms-select-option [value]="option.eJournalSignerId">
            {{ option.name }}
          </et-atoms-select-option>
          } @empty {
          <et-atoms-select-option [value]="" [disabled]="true">
            No signers added yet
          </et-atoms-select-option>
          }
        </et-atoms-select-input>
        @if ( form.controls.signerId.touched && form.controls.signerId.invalid )
        {
        <span etAtomsSelectError>Signer is required</span>
        }
      </et-atoms-select-field>

      <div class="relative w-full">
        <input
          interfacePreventTyping
          formControlName="cancelActUtcDate"
          [dpDayPicker]="datePickerConfig"
          placeholder="Cancellation date*"
          class="w-full text-base font-medium bg-transparent text-midnight input input-bordered placeholder-secondary-focus focus:outline-midnight focus:border-transparent focus:outline-offset-0"
        />
        @if ( form.controls['cancelActUtcDate'].touched &&
        form.controls['cancelActUtcDate'].invalid ) {
        <span class="text-sm text-error font-medium ml-6"
          >Date is required</span
        >
        }
      </div>

      <et-atoms-time-field class="w-full">
        <label>Cancellation time</label>
        <et-atoms-time-input
          formControlName="cancelActUtcTime"
          required
        ></et-atoms-time-input>
        @if (form.controls.cancelActUtcTime.touched &&
        form.controls.cancelActUtcTime.invalid) {
        <span etAtomsTimeError>Cancellation time is required</span>
        }
      </et-atoms-time-field>

      <div class="w-full col-span-2 px-[2px]">
        <label class="block mb-1 text-sm font-medium text-secondary"
          >Notes*</label
        >
        <textarea
          formControlName="notes"
          rows="3"
          class="w-full px-6 py-3 font-medium border rounded-lg focus:outline-midnight text-midnight"
        ></textarea>
        @if ( form.controls.notes.touched &&
        form.controls.notes.hasError('required') ) {
        <span class="text-sm text-error font-medium ml-4"
          >Note is required</span
        >
        }
      </div>
    </div>

    @if (error(); as err) {
    <p class="text-error m-0">{{ err }}</p>
    }

    <div class="flex flex-wrap justify-end gap-4">
      <button
        type="button"
        class="normal-case rounded-full btn btn-outline min-w-[184px] text-base"
        mat-dialog-close
        [disabled]="isLoading()"
      >
        Cancel
      </button>
      <button
        type="submit"
        class="normal-case rounded-full btn btn-primary min-w-[184px] text-base"
        [class.pointer-events-none]="isLoading()"
      >
        @if (isLoading()) {
        <et-atoms-spinner size="small"></et-atoms-spinner>
        } @else { Save }
      </button>
    </div>
  </form>
</ng-template>
